import React, {useEffect, useState} from 'react';
import {
    ColumnDef,
    ColumnFiltersState,
    getCoreRowModel,
    getExpandedRowModel,
    getFacetedMinMaxValues,
    getFacetedRowModel,
    getFacetedUniqueValues,
    getFilteredRowModel,
    getGroupedRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    GroupingState,
    PaginationState, Row,
    SortingState,
    TableOptions,
    useReactTable,
} from '@tanstack/react-table';
import {Table, TableBody, TableCell, TableHeader, TableRow,} from "../../cnComponents/cnTable";
import {SearchField} from "../search/searchField";
import {EmblaIcons} from "../../emblaIcons";
import './ReactTableStyles.scss'
import classNames from "classnames";
import {Loading} from "../loading/loading";
import {Localizer} from "../../../infrastructure/localization/localizer";
import {Pagination} from "../pagination/pagination";
import {PaginationModel} from "../../sharedmodels/filter/PaginationModel";
import Container from "../container/container";
import {Dropdown, DropdownOption} from "../dropdown/dropdown";
import DynamicButton from "../button/dynamicButton";
import {createPortal} from "react-dom";
import {SlideIn} from "../slideIn/slideIn";
import ReactTableColumnSettings from "./components/ReactTableColumnSettings";
import {arrayMove, horizontalListSortingStrategy, SortableContext} from "@dnd-kit/sortable";
import {
    closestCenter,
    DndContext,
    DragEndEvent,
    KeyboardSensor,
    MouseSensor,
    TouchSensor,
    useSensor,
    useSensors
} from "@dnd-kit/core";
import {restrictToHorizontalAxis} from '@dnd-kit/modifiers'
import {ReactTablePaginationCount} from "./components/ReactTablePaginationCount";
import {DragAlongCell} from "./DragAlongCell";
import ReactTableHeader from "./ReactTableHeader";
import {EmblaIcon} from "../emblaIcon/emblaIcon";
import { ReactTableBaseData } from './types';
import { ReactTableFilterNotation } from './components/ReactTableFilterNotation';
import InfoBadgePill from '../badge/infoBadgePill';
import useReactBaseTableState from './hooks/useReactBaseTableState';

export type NoResultsOptions = {
    noResultsMessage: string;
    noResultsIllustrationName?: string;
};

export type InitialFilterModel = {
    initialFilterId: string;
    initialFilterValue: string | boolean | [number, number] | string[];
}

export type FilterOptions = {
    // PageSize
    hidePageSizeFilter?: boolean;
    pageSizeOptions: number[];
    onPageSizeChangedCallback?: (pageSize: number) => void;
    // Search
    hideSearchLabel?: boolean;
    searchPlaceholder?: string;
    onSearchChangeCallback?: (searchQuery: string) => void;
    // Filtering
    initialFilter?: InitialFilterModel[];
    hideColumnSettingsButton?: boolean;
    renderAdditionalFiltersItems?: JSX.Element;
    // Ordering
    initialSortColumn?: string;
};

export type ReactTablePaginationProps = {
    pageIndex: number;
    pageLength: number;
    totalRecords: number;
    filteredRecords: number;
    onPageChangeCallback: (page: number) => void;
}

export type DefaultTableProps<TColumn extends object> = {
    tableId: string,
    title?: string;
    /** Will only show if title is set */
    renderContainerHeaderItems?: JSX.Element;
    rowOnClick?: (rowData: TColumn) => void;
    noResultsOptions?: NoResultsOptions;
    fetchFailed?: boolean;
} & Pick<TableOptions<TColumn>, "columns" | "data">;

export type ReactTableProps<T extends object> = {
    isLoading: boolean;
    isSortable?: boolean;
    enableColumnGrouping?: boolean;
    enableColumnResizing?: boolean;
    isAsync?: boolean;
    onSortCallback?: (sortInfos: SortingState) => void;
    onGroupingCallback?: (grouping: GroupingState) => void;
    paginationOverride?: ReactTablePaginationProps;
    filterOptions: FilterOptions;
    defaultExpandedGroups: boolean;
    rowColorCallback?: (row: Row<T>) => string;
    getSubRows?: (rowData: T) => T[] | undefined;
} & DefaultTableProps<T>;

const columnSettingsModalTargetId = "column-settings-modal";

function getFilterPil(filterValue: string | [number | undefined, number | undefined] | boolean) {
    return <>
        {Array.isArray(filterValue)
            ? filterValue.join(', ')
            : filterValue === true
                ? Localizer.global_ja()
                : filterValue === false
                    ? Localizer.global_nej()
                    : filterValue?.toString()}
    </>;
}

const ReactBaseTable = <T extends ReactTableBaseData, >({
                                 tableId,
                                 data,
                                 columns,
                                 isLoading,
                                 fetchFailed,
                                 paginationOverride,
                                 title,
                                 renderContainerHeaderItems,
                                 onSortCallback,
                                 onGroupingCallback,
                                 filterOptions,
                                 enableColumnGrouping,
                                 enableColumnResizing,
                                 isAsync = false,
                                 defaultExpandedGroups,
                                 rowColorCallback,
                                 getSubRows
                             }: ReactTableProps<T>) => {

    const [columnSettingsSlideInIsOpen, setColumnSettingsSlideInIsOpen] = useState<boolean>(false);

    const {
        columnFilters,
        columnOrder,
        columnVisibility,
        grouping,
        pagination,
        sorting,
        setColumnFilters,
        setColumnOrder,
        setColumnVisibility,
        setGrouping,
        setPagination,
        setSorting,
    } = useReactBaseTableState(tableId, columns, filterOptions);

    const table = useReactTable({
        data,
        columns,
        state: {
            sorting,
            columnFilters,
            columnVisibility,
            columnOrder,
            grouping,
            pagination,
        },
        onSortingChange: !isAsync
            ? setSorting
            : (newSorting) => {
                const updatedSorting = typeof newSorting === 'function' ? newSorting(sorting) : newSorting;
                setSorting(updatedSorting);
                if (onSortCallback) {
                    onSortCallback(updatedSorting);
                }
            },
        onGroupingChange: !isAsync
            ? setGrouping
            : (newGrouping) => {
                const updatedGrouping = typeof newGrouping === 'function' ? newGrouping(grouping) : newGrouping;
                setGrouping(updatedGrouping);
                if (onGroupingCallback) {
                    onGroupingCallback(updatedGrouping);
                }
            },
        onColumnOrderChange: setColumnOrder,
        onColumnFiltersChange: setColumnFilters,
        onColumnVisibilityChange: setColumnVisibility,
        getSubRows: getSubRows,

        getExpandedRowModel: getExpandedRowModel(),
        onPaginationChange: setPagination,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        getGroupedRowModel: getGroupedRowModel(),
        
        getFacetedRowModel: getFacetedRowModel(), // client-side faceting
        getFacetedUniqueValues: getFacetedUniqueValues(), // generate unique values for select filter/autocomplete
        getFacetedMinMaxValues: getFacetedMinMaxValues(), // generate min/max values for range filter
        manualPagination: isAsync,
        manualFiltering: isAsync,
        manualSorting: isAsync,
        columnResizeMode: 'onChange',
        enableSortingRemoval: false,
        initialState: {
            expanded: defaultExpandedGroups ? true : {},
        }
    });

    const pageSizeOptions = filterOptions.pageSizeOptions.map(x => ({
        label: `${x}`,
        value: x
    } as DropdownOption<number>));

    const handlePageSizeChanged = (pageLength?: number) => {
        if (filterOptions.onPageSizeChangedCallback && pageLength)
            filterOptions.onPageSizeChangedCallback(pageLength);
    }

    function handleDragEnd(event: DragEndEvent) {
        const {active, over} = event
        const overColumn = table.getColumn(over?.id as string);
        if (active && over && active.id !== over.id && !overColumn?.getIsPinned()) {
            setColumnOrder(columnOrder => {
                const oldIndex = columnOrder.indexOf(active.id as string)
                const newIndex = columnOrder.indexOf(over.id as string)
                return arrayMove(columnOrder, oldIndex, newIndex) //this is just a splice util
            })
        }
    }

    const sensors = useSensors(
        useSensor(MouseSensor, {}),
        useSensor(TouchSensor, {}),
        useSensor(KeyboardSensor, {})
    )
 
    const showContainerHeader = !!title;

    return (
        <div className={"card"}>
            {showContainerHeader &&
                <Container.Header>
                    <div className="d-flex flex-space">
                        <div className="d-flex flex-column">
                            <h3 className="">{title}</h3>
                            <ReactTableFilterNotation 
                                isAsync={isAsync}
                                paginationOverride={paginationOverride}
                                table={table}
                                isLoading={isLoading}
                            />
                        </div>
                        
                        {renderContainerHeaderItems}
                    </div>
                </Container.Header>
            }
            <Container.Body>
                <div className={classNames("flex-container-row wrap padding-bottom-m align-items-center")}>
                    {!filterOptions.hidePageSizeFilter &&
                        <Dropdown
                            minWidth={50}
                            options={pageSizeOptions}
                            onChange={(selectedOption) => isAsync ? handlePageSizeChanged(selectedOption?.value) : table.setPageSize(Number(selectedOption?.value))}
                            value={isAsync ? pageSizeOptions.find(x => x.value === paginationOverride?.pageLength) : pageSizeOptions.find(x => x.value === table.getState().pagination.pageSize)}
                            isClearable={false}
                            isSearchable={false}
                        />
                    }
                    {!showContainerHeader && 
                        <div className="margin-left-s">
                            <ReactTableFilterNotation 
                                isAsync={isAsync}
                                paginationOverride={paginationOverride}
                                table={table}
                                isLoading={isLoading}
                            />
                        </div>
                    }

                    {isAsync && !!filterOptions.renderAdditionalFiltersItems &&
                        filterOptions.renderAdditionalFiltersItems
                    }
                    
                    <div className={"flex-container-row flex-align-r align-items-center wrap"}>
                        {table.getAllColumns().map(column => {
                            const filterValue = column.getFilterValue() as string | boolean | [number | undefined, number | undefined];
                            if ((filterValue !== undefined && !Array.isArray(filterValue)) || (Array.isArray(filterValue) && filterValue?.some(x => x))) {
                                return (
                                    <InfoBadgePill
                                        badgeText={<><b>{column.columnDef.header}: &nbsp;</b> {getFilterPil(filterValue)}</>}
                                        paddingSize='padding-s'
                                        badgeType='badge-info-light'
                                        emblaIconName='close'
                                        emblaIconPosition='after'
                                        emblaIconOnClick={() => {
                                            setColumnFilters((prevFilters) => prevFilters.filter(filter => filter.id !== column.id));
                                        }}
                                    />
                                );
                            }
                            return null;
                        })}
                        {table.getState().columnFilters.some(x => (x.value !== undefined && !Array.isArray(x.value)) || (Array.isArray(x.value) && x.value.some(y => y))) &&
                            <InfoBadgePill
                                paddingSize='padding-s'
                                badgeType='badge-primary'
                                emblaIconName='close'
                                emblaIconOnClick={() => setColumnFilters([])}
                            />
                        }
                        {!filterOptions.hideColumnSettingsButton &&
                            <DynamicButton onClick={() => setColumnSettingsSlideInIsOpen(true)}
                                           additionalClasses={"d-flex margin-right-m"}>
                                <DynamicButton.TextIconItem text={Localizer.reactTable_columns()}
                                                            iconName={EmblaIcons.Graph}
                                />
                            </DynamicButton>
                        }
                        {isAsync && filterOptions.onSearchChangeCallback && !filterOptions.hideSearchLabel &&
                            <div className="flex items-center ">
                                <SearchField
                                    additionalClasses={"min-w-300"}
                                    placeholder={filterOptions.searchPlaceholder}
                                    callback={filterOptions?.onSearchChangeCallback}
                                />
                            </div>
                        }
                    </div>
                </div>

                <DndContext
                    collisionDetection={closestCenter}
                    modifiers={[restrictToHorizontalAxis]}
                    onDragEnd={handleDragEnd}
                    sensors={sensors}
                >
                    <div className="table-wrapper">
                        {isLoading && <div className="loading-overlay" />}
                    <Table className={"table-container"}>
                        <TableHeader>
                            {table.getHeaderGroups().map((headerGroup) => (
                                <TableRow key={headerGroup.id}>
                                    <SortableContext
                                        items={columnOrder}
                                        strategy={horizontalListSortingStrategy}>
                                        {headerGroup.headers.map(header => (
                                            <ReactTableHeader key={header.id}
                                                              isAsync={isAsync}
                                                              header={header}
                                                              enableColumnGrouping={enableColumnGrouping}
                                                              enableColumnResizing={enableColumnResizing}/>
                                        ))}
                                    </SortableContext>
                                </TableRow>
                            ))}
                        </TableHeader>
                        <TableBody>
                            {isLoading ? (
                                <TableRow>
                                    <TableCell colSpan={columns.length} className="loading-cell">
                                        <div className="loading-cell-content">
                                            <Loading isLoading={isLoading} text={Localizer.global_gettingData()}
                                                     timeout={0}/>
                                        </div>
                                    </TableCell>
                                </TableRow>
                            ) : table.getRowModel().rows?.length ? (
                                table.getRowModel().rows?.filter(x => !x.original.isPlaceholder).map((row) => {
                                    const isParentRow = row.depth === 0 && !row.parentId && !!getSubRows;
                                    const hasSubRows = row.subRows.filter(x => !x.original.isPlaceholder).length > 0;

                                    return (
                                        <TableRow 
                                            key={row.id} 
                                            data-state={row.getIsSelected() && 'selected'}
                                            style={{ backgroundColor: rowColorCallback ? rowColorCallback(row) : "unset" }}
                                            className={classNames(
                                                isParentRow && "parent-row",
                                                hasSubRows && "clickable-row"
                                            )} 
                                            onClick={isParentRow ? row.getToggleExpandedHandler() : undefined}
                                        >
                                            {row.getVisibleCells().map((cell, index) => {
                                                return (
                                                <SortableContext
                                                    key={cell.id}
                                                    items={columnOrder}
                                                    strategy={horizontalListSortingStrategy}>
                                                    <DragAlongCell
                                                        cell={cell}
                                                        cellIndex={index}
                                                        row={row}
                                                        enableColumnGrouping={enableColumnGrouping}/>
                                                </SortableContext>
                                            )})}
                                        </TableRow>
                                )
                            })
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={columns.length} className="no-results-cell">
                                        <div className="no-results-content">
                                            {!fetchFailed ? Localizer.noResults() : Localizer.reactTable_fetchErrorInfo()}
                                        </div>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                    </div>
                </DndContext>
            </Container.Body>
            <Container.Footer additionalClasses="flex-center flex-container-column">
                {!isAsync &&
                    <Pagination
                        handlePagination={(page) => table.setPageIndex(page - 1)}
                        paginationModel={new PaginationModel(table.getState().pagination.pageIndex + 1, table.getState().pagination.pageSize, table.getRowCount())}
                    />
                }
                {isAsync && paginationOverride &&
                    <Pagination
                        handlePagination={(page) => paginationOverride.onPageChangeCallback(page)}
                        paginationModel={new PaginationModel(paginationOverride.pageIndex, paginationOverride.pageLength, paginationOverride.filteredRecords)}
                    />
                }
                <div className={"margin-top-s"}>
                    <ReactTablePaginationCount
                        key={isAsync && paginationOverride
                            ? paginationOverride.pageIndex
                            : table.getState().pagination.pageIndex}
                        pageIndex={isAsync && paginationOverride
                            ? paginationOverride.pageIndex
                            : table.getState().pagination.pageIndex + 1}
                        pageCount={isAsync && paginationOverride
                            ? Math.floor(paginationOverride.filteredRecords / paginationOverride.pageLength) + 1
                            : table.getPageCount()}
                        setPageindex={(page) => isAsync && paginationOverride
                            ? paginationOverride.onPageChangeCallback(page + 1)
                            : table.setPageIndex(page)}
                    />
                </div>
            </Container.Footer>
            {columnSettingsSlideInIsOpen &&
                createPortal(
                    <SlideIn
                        id={columnSettingsModalTargetId}
                        defaultOpen={true}
                        title={Localizer.reactTable_columns()}
                        bodyContent={<ReactTableColumnSettings table={table}/>}
                        actionOnCloseCallback={() => setColumnSettingsSlideInIsOpen(false)}
                    />,
                    document.body
                )
            }
        </div>
    );
};

export default ReactBaseTable;
